import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import type { HiringNoticeList, HiringNoticeRow, HiringRequest } from '../../services/api';
import { FormErrorBanner } from '../FormErrorBanner';
import { Skeleton } from '../Skeleton';
import { HiringPager } from './HiringPager';
import { DASHBOARD_WORDS } from './hiringText.dashboard';
import {
  NOTICE_WORDS, noticeDayWords, noticePersonLine, noticePersonName, noticeStoreParts, noticeStoreWords,
} from './hiringText.notice';

const TH = 'whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-gray-500 first:pl-6 last:pr-6';
const TD = 'whitespace-nowrap px-4 py-3 first:pl-6 last:pr-6';
/** The Actions column keeps to the right edge while the table scrolls sideways, as the Stores table's does. */
const ACTIONS_TH = 'sticky right-0 z-10 whitespace-nowrap bg-gray-50 px-6 py-2.5 text-center text-xs font-semibold text-gray-500';
const ACTIONS_TD = 'sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50';

/** A row's key: a person can be at two stores, one row each. */
export const noticeRowKey = (row: Pick<HiringNoticeRow, 'store' | 'person'>): string =>
  `${row.store.storeId.toLowerCase()}|${row.person.empCode}`;

/**
 * Dashboard › Hiring's Notice period tab (16 Oct 2026): the people deployed at the stores this
 * person looks after who are serving notice, one row per person per store, the soonest last
 * working day first - Store · Person · Last working day · Hiring · Actions.
 *
 * WHAT A ROW OFFERS (the Table Actions standard: one strip, a different glyph for each thing):
 *   - Raise hiring (the plus), to a holder of "Raise & cancel hiring" where the list says Raise
 *     is on: the Raise window, for the store, "Replacing someone", this person chosen;
 *   - Ask for hiring (the paper plane), in its place, to a person who may ask and may not
 *     raise - disabled while the store's own request waits;
 *   - Link an opening (the link), to a person who may manage: the Link window, for the store;
 *   - neither Raise nor Ask once hiring replaces the person ("Raised"): in their place, the
 *     store's hiring (the briefcase) - or, when it is a request that waits and this person
 *     sees requests, that request (the clipboard).
 * WHO MAY is decided by the owner (`can`); the server refuses anyone else all the same.
 *
 * A FAILED READ IS NEVER "NOBODY": one plain line above the table says when 1Office was not
 * read in full, and the rows that could be read are shown.
 */
export function HiringNoticeTable({
  list, loading, error, searching, now, can, askWaiting, requestOf, opening, onRaise, onAsk, onLink, onOpenStore, onOpenRequest,
  onPage, onTryAgain,
}: {
  /** The last answer; null before the first. */
  list: HiringNoticeList | null;
  loading: boolean;
  error: string | null;
  /** Something is typed in the search box: an empty table says "Nothing matches.". */
  searching: boolean;
  now: Date;
  can: { raise: boolean; ask: boolean; link: boolean };
  /** The store's own hiring request waits: Ask is disabled, as on the Stores page. */
  askWaiting: (row: HiringNoticeRow) => boolean;
  /** The waiting request that replaces the person, when this person is shown requests. */
  requestOf: (row: HiringNoticeRow) => HiringRequest | null;
  /** The row whose store's hiring is being read, to open it (its hiring window, or Raise or Ask for it). */
  opening: string | null;
  onRaise: (row: HiringNoticeRow) => void;
  onAsk: (row: HiringNoticeRow) => void;
  onLink: (row: HiringNoticeRow) => void;
  onOpenStore: (row: HiringNoticeRow) => void;
  onOpenRequest: (request: HiringRequest) => void;
  onPage: (page: number) => void;
  onTryAgain: () => void;
}) {
  const rows = list?.rows ?? [];
  const perPage = list?.page?.perPage || 12;
  const total = list?.page?.total ?? 0;
  const page = list?.page?.number ?? 1;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const readLine = NOTICE_WORDS.readLine(list?.oneOffice);

  return (
    <>
      {readLine && <p role="status" className="text-xs text-amber-800">{readLine}</p>}
      {error && (
        <div className="flex flex-wrap items-center gap-3">
          <FormErrorBanner message={error} />
          <button type="button" className="text-sm font-medium text-primary-600 hover:underline" onClick={onTryAgain}>
            {DASHBOARD_WORDS.tryAgain}
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" aria-label={NOTICE_WORDS.tab}>
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className={TH}>{NOTICE_WORDS.columns.store}</th>
                <th className={TH}>{NOTICE_WORDS.columns.person}</th>
                <th className={TH}>{NOTICE_WORDS.columns.lastDay}</th>
                <th className={TH}>{NOTICE_WORDS.columns.hiring}</th>
                {/* Written out, so the Table Actions source check finds the column by its name. */}
                <th className={ACTIONS_TH}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && rows.length === 0 ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className={TD}><Skeleton className="h-4 w-48" /></td>
                    <td className={TD}><Skeleton className="h-4 w-40" /></td>
                    <td className={TD}><Skeleton className="h-4 w-20" /></td>
                    <td className={TD}><Skeleton className="h-4 w-20" /></td>
                    <td className={TD}><Skeleton className="mx-auto h-5 w-16" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    {/* "Nobody" only when 1Office was read in full: otherwise the line above says what could not be. */}
                    {error || (list && list.oneOffice.read !== 'read') ? '' : searching ? NOTICE_WORDS.noneFound : NOTICE_WORDS.none}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const store = noticeStoreWords(row);
                  const storeParts = noticeStoreParts(row);
                  const name = noticePersonName(row);
                  const day = noticeDayWords(row.lastWorkingDay, now);
                  const request = row.hiring.raised && row.hiring.by === 'request' ? requestOf(row) : null;
                  const key = noticeRowKey(row);
                  const waiting = can.ask && askWaiting(row);
                  return (
                    <tr key={key} className="group hover:bg-gray-50">
                      <td className={TD}>
                        {/* A long name is cut; its code never is - it tells similar names apart. The whole of it on hover. */}
                        {storeParts.code ? (
                          <span title={store} className="flex max-w-[16rem]">
                            <span className="min-w-0 truncate">{storeParts.name}</span>{' '}
                            <span className="flex-none pl-1">{storeParts.code}</span>
                          </span>
                        ) : (
                          <span title={store} className="block max-w-[16rem] truncate">{storeParts.name}</span>
                        )}
                      </td>
                      <td className={TD}>
                        <div className="max-w-[18rem] truncate font-medium text-gray-900" title={name}>{name}</div>
                        <div className="max-w-[18rem] truncate text-xs text-gray-500">{noticePersonLine(row)}</div>
                      </td>
                      <td className={`${TD} tabular-nums`}>
                        {day
                          ? <span title={row.lastWorkingDay ?? undefined}>{day}</span>
                          : <span className="text-gray-400" title={NOTICE_WORDS.noDateHover(row, now)}>{NOTICE_WORDS.noDate}</span>}
                      </td>
                      <td className={TD}>
                        <span className="inline-flex items-center gap-2" title={NOTICE_WORDS.raisedHover(row)}>
                          <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${row.hiring.raised ? 'bg-green-600' : 'bg-amber-600'}`} />
                          {row.hiring.raised ? NOTICE_WORDS.raised : NOTICE_WORDS.notRaised}
                        </span>
                      </td>
                      <td className={ACTIONS_TD}>
                        <TableActionGroup>
                          {/* Raise or Ask, never both, and neither once hiring replaces the person. */}
                          {!row.hiring.raised && can.raise && (
                            <TableActionButton action="opening" label={NOTICE_WORDS.raiseFor(row)} loading={opening === key} onClick={() => onRaise(row)} />
                          )}
                          {!row.hiring.raised && !can.raise && can.ask && (
                            <TableActionButton action="submit" label={NOTICE_WORDS.askFor(row)}
                              disabled={waiting}
                              title={waiting ? NOTICE_WORDS.askWaiting : undefined}
                              loading={opening === key}
                              onClick={() => onAsk(row)} />
                          )}
                          {/* Raised: what replaces them - the request that waits, or the store's hiring. */}
                          {row.hiring.raised && request && (
                            <TableActionButton action="review" label={NOTICE_WORDS.openRequestFor(row)} onClick={() => onOpenRequest(request)} />
                          )}
                          {row.hiring.raised && !request && (
                            <TableActionButton action="openings" label={NOTICE_WORDS.openStoreFor(row)}
                              loading={opening === key}
                              onClick={() => onOpenStore(row)} />
                          )}
                          {can.link && (
                            <TableActionButton action="link" label={NOTICE_WORDS.linkFor(row)} onClick={() => onLink(row)} />
                          )}
                        </TableActionGroup>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {list && total > 0 && (
          <HiringPager page={page} lastPage={lastPage} perPage={perPage} total={total} onPage={onPage} />
        )}
      </div>
    </>
  );
}
